import { useCallback, useEffect, useRef, useState } from 'react';

import Honeybadger from '@honeybadger-io/js';

import {
  b64decode,
  b64encode,
  clearKey,
  decrypt,
  encrypt,
  getOrCreateKey
} from 'lib/helpers/draftCrypto';
import { scrubDraftPayload } from 'lib/helpers/scrubDraftPayload';

const usePersistedFormState = ({
  storageKey,
  keyId,
  data,
  ttlMs,
  schemaVersion,
  ownerId = null,
  enabled = true,
  debounceMs = 400
}) => {
  const [hasDraft, setHasDraft] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [restored, setRestored] = useState(null);
  const saveTimer = useRef(null);
  const keyRef = useRef(null);
  // createdAt is captured once per draft session so DRAFT_TTL_MS is an absolute
  // lifetime; without this it would behave as a sliding idle timeout and an
  // actively-edited PHI draft would never age out.
  const createdAtRef = useRef(null);
  // abandonedRef + writeSeq make discard durable: discard() flips the ref so
  // the save effect early-returns, and bumps writeSeq so any already-scheduled
  // save resolves to a stale seq and skips its localStorage.setItem. Together
  // they prevent the "Start over" / Confirmation / logout races where the
  // debounced save would resurrect the draft after teardown.
  const abandonedRef = useRef(false);
  // Latches once the user has acted on the surfaced draft (resume OR discard)
  // this session. Gates ONLY the mount probe so an ownerId change mid-flow
  // (e.g. the user logs in and ownerId flips from the magic-link token to their
  // user id) can't re-run the probe and re-open a dialog they already
  // dismissed — nor let a stale token-stamped blob fail the owner check and
  // silently discard the in-progress draft. Deliberately separate from
  // abandonedRef: abandonedRef gates the save effect and must stay false after
  // Resume so ongoing edits keep persisting; decidedRef gates only surfacing.
  const decidedRef = useRef(false);
  const writeSeq = useRef(0);
  const prevEnabledRef = useRef(enabled);
  // Dedupe Honeybadger reports per hook instance — a user in a locked-down
  // browser would otherwise emit one notification per keystroke.
  const reportedReasonsRef = useRef(new Set());

  const reportOnce = useCallback(
    (reason, err) => {
      if (reportedReasonsRef.current.has(reason)) return;
      reportedReasonsRef.current.add(reason);
      Honeybadger.notify(`usePersistedFormState: ${reason}`, {
        context: { reason, storageKey, message: err?.message, name: err?.name }
      });
    },
    [storageKey]
  );

  const discard = useCallback(async () => {
    // Latch BEFORE the awaits so any in-flight debounced save sees the flag
    // and skips its localStorage.setItem.
    abandonedRef.current = decidedRef.current = true;
    writeSeq.current += 1;
    clearTimeout(saveTimer.current);
    try {
      localStorage.removeItem(storageKey);
    } catch (err) {
      reportOnce('discard_storage_failed', err);
    }
    try {
      await clearKey(keyId);
    } catch (err) {
      reportOnce('discard_clear_key_failed', err);
    }
    keyRef.current = null;
    createdAtRef.current = null;
    setHasDraft(false);
    setRestored(null);
    setIsHydrated(true);
    // Re-arm persistence so a session that continues after this discard ("Start
    // over", or a probe-initiated cleanup of an expired/foreign/corrupt draft)
    // saves a fresh draft again. abandonedRef was latched true at the top of
    // discard() only to make the teardown durable against an already-scheduled
    // save; the writeSeq bump above is what actually stops that stale save from
    // resurrecting the old blob, so clearing the latch here is safe. Without
    // this the save effect (which early-returns on abandonedRef) stays disabled
    // for the rest of the session because the reset effect only fires when
    // `enabled` flips false → true, which never happens during the normal flow.
    abandonedRef.current = false;
  }, [storageKey, keyId, reportOnce]);

  // Reset the abandoned + decided latches ONLY when a fresh session begins
  // (enabled flips false → true). Without this guard they would lift on every
  // render and a save effect could re-arm after a discard.
  useEffect(() => {
    if (enabled && !prevEnabledRef.current) abandonedRef.current = decidedRef.current = false;
    prevEnabledRef.current = enabled;
  }, [enabled]);

  // Probe for an existing draft on mount.
  useEffect(() => {
    // Skip when disabled, or once the user has resumed/discarded this session:
    // an ownerId change (login) re-runs this effect and must not re-surface a
    // dialog they already dismissed. setIsHydrated(true) is a no-op on re-runs.
    if (!enabled || decidedRef.current) {
      setIsHydrated(true);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      let raw;
      try {
        raw = localStorage.getItem(storageKey);
      } catch (err) {
        reportOnce('storage_access_denied', err);
        if (!cancelled) setIsHydrated(true);
        return;
      }
      if (!raw) {
        if (!cancelled) setIsHydrated(true);
        return;
      }

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (err) {
        reportOnce('corrupted_blob', err);
        await discard();
        return;
      }

      if (parsed.v !== schemaVersion) {
        await discard();
        return;
      }
      if (!parsed.createdAt || Date.now() - parsed.createdAt > ttlMs) {
        await discard();
        return;
      }

      try {
        const key = await getOrCreateKey(keyId);
        const plaintext = await decrypt(b64decode(parsed.data), b64decode(parsed.iv), key);
        if (cancelled) return;
        const decoded = JSON.parse(new TextDecoder().decode(plaintext));
        // Identity binding: a draft persisted by user A on this device must not
        // be offered to user B (or a different magic-link session). A draft with
        // no stored ownerId (older pre-binding drafts, or anonymous flows saved
        // without an identifier) carries no binding and is surfaced as-is. But
        // once a draft IS owner-bound it must match the current identity exactly —
        // including when the current session has no identity (ownerId null): an
        // owner-bound draft is discarded rather than leaked. Guarding on the
        // current ownerId here would silently no-op the binding in that case.
        if (decoded?.ownerId != null && decoded.ownerId !== ownerId) {
          await discard();
          return;
        }
        keyRef.current = key;
        createdAtRef.current = parsed.createdAt;
        setRestored(decoded);
        setHasDraft(true);
      } catch (err) {
        reportOnce('decrypt_failed', err);
        if (!cancelled) await discard();
      } finally {
        if (!cancelled) setIsHydrated(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storageKey, keyId, schemaVersion, ttlMs, ownerId, enabled, discard, reportOnce]);

  // Drop the in-memory key when the identity changes (login/logout). ownerId
  // flips out-of-band of this hook — e.g. a /book mid-flow login calls
  // clearSelfSchedulingPersistence(), which deletes the IDB key but cannot reach
  // this hook's keyRef, and the component never remounts. Without this, the next
  // save reuses the now-deleted key, so on reload getOrCreateKey mints a fresh
  // key, decrypt() throws, and the post-login draft is silently discarded.
  // Nulling keyRef forces the next save to mint-and-persist a new key, keeping
  // the encrypting key and IDB in sync. createdAtRef is reset alongside so the
  // freshly-keyed blob gets a new absolute-TTL anchor.
  useEffect(() => {
    keyRef.current = createdAtRef.current = null;
  }, [ownerId]);

  // Debounced encrypt-and-save on data change. Skipped until after hydration
  // so the initial empty context state doesn't clobber a draft we're about to
  // restore.
  useEffect(() => {
    if (!enabled) return undefined;
    if (abandonedRef.current) return undefined;
    if (!isHydrated) return undefined;
    if (hasDraft) return undefined; // waiting for user to resume or discard
    if (data == null) return undefined;

    clearTimeout(saveTimer.current);
    const seq = writeSeq.current + 1;
    writeSeq.current = seq;
    saveTimer.current = setTimeout(async () => {
      try {
        const key = keyRef.current ?? (keyRef.current = await getOrCreateKey(keyId));
        const iv = crypto.getRandomValues(new Uint8Array(12));
        const payloadWithOwner = ownerId != null ? { ...data, ownerId } : data;
        const plaintextBytes = new TextEncoder().encode(
          JSON.stringify(scrubDraftPayload(payloadWithOwner))
        );
        const ciphertext = await encrypt(plaintextBytes, iv, key);
        // Latest-write-wins guard: a later edit or a discard bumps writeSeq,
        // so a slower in-flight encrypt resolves with a stale seq and bails
        // before touching localStorage. Combined with abandonedRef this also
        // closes the "discard then late save resurrects the draft" race.
        if (abandonedRef.current || seq !== writeSeq.current) return;
        if (createdAtRef.current == null) createdAtRef.current = Date.now();
        const payload = JSON.stringify({
          v: schemaVersion,
          createdAt: createdAtRef.current,
          iv: b64encode(iv),
          data: b64encode(ciphertext)
        });
        try {
          localStorage.setItem(storageKey, payload);
        } catch (err) {
          // Quota exceeded / private mode — persistence is best-effort.
          reportOnce('save_quota_exceeded', err);
        }
      } catch (err) {
        // Non-fatal: encryption or key retrieval failed, drop the save.
        reportOnce('save_encrypt_failed', err);
      }
    }, debounceMs);

    return () => clearTimeout(saveTimer.current);
  }, [
    data,
    enabled,
    hasDraft,
    isHydrated,
    ownerId,
    storageKey,
    keyId,
    schemaVersion,
    debounceMs,
    reportOnce
  ]);

  // Cross-tab coordination: if another tab clears or replaces the blob, stop
  // re-saving from this one so two open /book tabs don't fight over revival.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== storageKey) return;
      if (event.newValue === null) {
        // Sibling tab discarded the draft — latch and clear local state so
        // this tab can't immediately re-create the blob/key.
        abandonedRef.current = true;
        writeSeq.current += 1;
        clearTimeout(saveTimer.current);
        keyRef.current = null;
        createdAtRef.current = null;
        setHasDraft(false);
        setRestored(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [storageKey]);

  const resume = useCallback(() => {
    // Latch the decision (but NOT abandonedRef) so the probe can't re-open the
    // dialog when ownerId changes, while the save effect keeps persisting edits.
    decidedRef.current = true;
    const snapshot = restored;
    setRestored(null);
    setHasDraft(false);
    return snapshot;
  }, [restored]);

  return { hasDraft, isHydrated, resume, discard };
};

export default usePersistedFormState;
